import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import fetchData from '../../functions/fetchData';
import CardComponent from '../../components/cards/CardComponent';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import { 
  FaDollarSign, 
  FaSpinner,
  FaFilePdf,
  FaSave
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';

const ManualPayment = () => {
  const navigate = useNavigate();
  const billNoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    date: '', // Will be set from sale_date_setup
    billNo: '',
    paymentMode: 'Cash',
    discountAmount: '0',
    netAmount: '',
    cashPayment: '0',
    bankTransfer: '0',
    cardSwipe: '0',
    balanceAmount: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [orderFound, setOrderFound] = useState(false);
  const [setupDate, setSetupDate] = useState('');
  const [loadingSetupDate, setLoadingSetupDate] = useState(true);

  // Get user information for authentication
  const userTypeRaw = localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '';
  const userType = userTypeRaw.toLowerCase();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const isAuthenticated = !!token;
  const isCashier = userTypeRaw === 'Cashier' || userTypeRaw === 'admin' || userTypeRaw === 'Admin' || 
                   userType === 'cashier' || userType === 'admin' || userType === 'system administrator';

  // console.log('ManualPayment - Auth Debug:', {
  //   userTypeRaw,
  //   userType,
  //   token: token ? 'exists' : 'null',
  //   tokenLength: token ? token.length : 0,
  //   isAuthenticated,
  //   isCashier,
  //   localStorageUserType: localStorage.getItem('usertype'),
  //   sessionStorageUserType: sessionStorage.getItem('usertype'),
  //   localStorageToken: localStorage.getItem('token') ? 'exists' : 'null',
  //   sessionStorageToken: sessionStorage.getItem('token') ? 'exists' : 'null'
  // });

  useEffect(() => {
    // console.log('ManualPayment - useEffect triggered');
    
    if (!isAuthenticated) {
      // console.log('ManualPayment - Not authenticated, redirecting to login');
      toast.error('Session expired. Please login again.');
      navigate('/');
      return;
    }

    if (!isCashier) {
      // console.log('ManualPayment - Not a cashier, access denied');
      toast.error(`Access denied. Only cashiers and administrators can access this page. Your user type: ${userTypeRaw || 'undefined'}`);
      // Redirect based on user type
      if (userType === 'account') {
        navigate('/dashboard/account');
      } else {
        navigate('/dashboard/admin');
      }
      return;
    }

    // console.log('ManualPayment - Authentication successful, loading page');

    // Focus on bill number input when component mounts
    if (billNoInputRef.current) {
      billNoInputRef.current.focus();
    }

    // Fetch setup date and load pending records
    fetchLastCloseDate();
    fetchPendingRecords();
  }, [isAuthenticated, isCashier, navigate, userType, userTypeRaw]);

  // Auto-refresh pending records every minute
  useEffect(() => {
    if (!isAuthenticated || !isCashier) return;
    
    const intervalId = setInterval(() => {
      // console.log('🔄 Auto-refreshing pending records...', new Date().toLocaleTimeString());
      fetchPendingRecords();
    }, 60000); // 60000ms = 1 minute
    
    // Cleanup interval on component unmount
    return () => {
      clearInterval(intervalId);
      // console.log('✅ Auto-refresh interval cleared');
    };
  }, [isAuthenticated, isCashier]);

  // Auto-search bill when bill number changes (with debouncing)
  useEffect(() => {
    if (!formData.billNo.trim() || formData.billNo.length < 3) {
      // Reset form if bill number is too short
      if (orderFound) {
        setOrderFound(false);
        setFormData(prev => ({
          ...prev,
          netAmount: '',
          discountAmount: '0',
          cashPayment: '0',
          bankTransfer: '0',
          cardSwipe: '0',
          balanceAmount: ''
        }));
      }
      return;
    }

    // Debounce the search - wait 500ms after user stops typing
    const timeoutId = setTimeout(() => {
      // console.log('🔍 Auto-searching for bill:', formData.billNo);
      // Call search function directly to avoid dependency issues
      searchBillById(formData.billNo.trim());
    }, 500);

    // Cleanup timeout if user continues typing
    return () => {
      clearTimeout(timeoutId);
    };
  }, [formData.billNo, orderFound]); // Include orderFound in dependencies

  // Fetch last close date from API
  const fetchLastCloseDate = async () => {
    setLoadingSetupDate(true);
    try {
      const response = await axios.get('/last-close-date', getHeaders());
      
      // console.log('API Response:', response.data);
      
      if (response.data && response.data.success) {
        // Match the exact controller response structure
        const lastCloseDate = response.data.close_date || 
                             response.data.data?.dte ||
                             response.data.data?.date;
        
        if (lastCloseDate) {
          // Convert to YYYY-MM-DD format for date input
          const formattedDate = new Date(lastCloseDate).toISOString().split('T')[0];
          setSetupDate(formattedDate);
          setFormData(prev => ({
            ...prev,
            date: formattedDate
          }));
          
          // console.log('Successfully set setup date:', formattedDate);
          toast.success(`Business date loaded: ${formattedDate}`);
          return; // Successfully set date, exit function
        }
      }
      
      // If we reach here, no valid date was found
      throw new Error('No valid date found in API response');
      
    } catch (error) {
      // console.error('Error fetching last close date:', error);
      
      // Fallback to current date if API call fails
      const fallbackDate = new Date().toISOString().split('T')[0];
      setSetupDate(fallbackDate);
      setFormData(prev => ({
        ...prev,
        date: fallbackDate
      }));
      
      toast.warn(`Could not fetch setup date from sale_date_setup table. Using current date: ${fallbackDate}`);
    } finally {
      setLoadingSetupDate(false);
    }
  };

  // Separate search function to avoid useEffect dependency issues
  const searchBillById = async (billNumber) => {
    if (!billNumber) return;
    
    setLoading(true);
    // console.log('Searching for bill number:', billNumber);
    
    try {
      // Search by barbill field only
      let billData = null;
      
      try {
        billData = await fetchData('collection', null, 'id', {barbill: `${billNumber}`});
        // console.log('Search by barbill result:', billData);
      } catch (error) {
        // console.log('Search by barbill failed:', error);
      }
      
      if (billData && billData.length > 0) {
        const bill = billData[0];
        const netAmount = parseFloat(bill.netamount) || 0;
        const currentDiscount = parseFloat(bill.discount) || 0;
        const currentPaid = parseFloat(bill.paidamount) || 0;
        const currentBalance = parseFloat(bill.balance) || 0;
        
        // console.log('Found bill:', {
        //   id: bill.id,
        //   barbill: bill.barbill,
        //   netamount: bill.netamount,
        //   paidamount: bill.paidamount,
        //   balance: bill.balance,
        //   discount: bill.discount
        // });
        
        // Reset payment fields and show current bill data
        setFormData(prev => ({
          ...prev,
          netAmount: netAmount.toString(),
          discountAmount: currentDiscount.toString(),
          cashPayment: '0',
          bankTransfer: '0',
          cardSwipe: '0',
          balanceAmount: currentBalance.toFixed(2)
        }));
        
        setOrderFound(true);
        
        if (currentBalance > 0) {
          toast.success(`📋 Bill found! Net Amount: ฿${netAmount.toLocaleString()}, Balance Due: ฿${currentBalance.toLocaleString()}`);
        } else {
          toast.info(`📋 Bill found! Net Amount: ฿${netAmount.toLocaleString()}, Already Paid: ฿${currentPaid.toLocaleString()}`);
        }
      } else {
        // console.log('No bill found with number:', billNumber);
        // Don't show error toast for auto-search, only for manual search
        setOrderFound(false);
      }
    } catch (error) {
      // console.error('Error searching for bill:', error);
      // Don't show error toast for auto-search
      setOrderFound(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending records from collection table where balance > 0
  const fetchPendingRecords = async () => {
    setLoadingPending(true);
    // console.log('Fetching pending records...');
    try {
      const { fetchdatanotequal } = await import('../../functions/fetchData');
      // console.log('Calling fetchdatanotequal with params:', {
      //   table: 'collection',
      //   orderBy: 'id',
      //   where: { balance: 0 }
      // });
      
      const pendingData = await fetchdatanotequal('collection', null, 'id', { balance: 0 });
      // console.log('Received pending data:', pendingData);
      // console.log('Pending records count:', pendingData?.length || 0);
      // console.log('Sample pending record:', pendingData?.[0]);
      
      if (pendingData && pendingData.length > 0) {
        setPendingRecords(pendingData);
        // console.log('Set pending records state with', pendingData.length, 'records');
      } else {
        setPendingRecords([]);
        // console.log('No pending data found, set empty array');
      }
    } catch (error) {
      // console.error('Error fetching pending records:', error);
      toast.error('Failed to load pending records');
    } finally {
      setLoadingPending(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate balance when payment amounts change
      if (['netAmount', 'cashPayment', 'bankTransfer', 'cardSwipe', 'discountAmount'].includes(field)) {
        const netAmount = parseFloat(updated.netAmount) || 0;
        const cashPayment = parseFloat(updated.cashPayment) || 0;
        const bankTransfer = parseFloat(updated.bankTransfer) || 0;
        const cardSwipe = parseFloat(updated.cardSwipe) || 0;
        const discountAmount = parseFloat(updated.discountAmount) || 0;
        
        const totalPaid = cashPayment + bankTransfer + cardSwipe;
        const balanceAmount = (netAmount - discountAmount) - totalPaid;
        
        updated.balanceAmount = balanceAmount.toFixed(2);
        
        // Provide user feedback for payment completion
        if (field !== 'netAmount' && field !== 'discountAmount' && value > 0) {
          if (balanceAmount <= 0) {
            setTimeout(() => toast.success('💚 Payment completed! Balance: ฿0'), 100);
          } else if (balanceAmount > 0) {
            setTimeout(() => toast.info(`💙 Balance remaining: ฿${balanceAmount.toLocaleString()}`), 100);
          }
        }
      }
      
      return updated;
    });
  };

  // Handle manual bill search (from button click or Enter key)
  const handleBillSearch = async () => {
    if (!formData.billNo.trim()) {
      toast.error('Please enter a bill number');
      return;
    }

    const billNumber = formData.billNo.trim();
    // console.log('🔍 Manual search for bill:', billNumber);
    
    // Call the search function and show error messages for manual search
    setLoading(true);
    
    try {
      await searchBillById(billNumber);
      
      // If no bill was found, show error message for manual search
      if (!orderFound) {
        toast.error(`❌ Bill number "${billNumber}" not found in collection records`);
      }
    } catch (error) {
      toast.error(`❌ Error searching for bill: ${error.message}`);
    }
  };

  // Handle save payment
  const handleSavePayment = async () => {
    if (!formData.billNo.trim()) {
      toast.error('Please enter a bill number');
      return;
    }

    if (!formData.netAmount || parseFloat(formData.netAmount) <= 0) {
      toast.error('Please enter a valid net amount');
      return;
    }

    const totalPaid = (parseFloat(formData.cashPayment) || 0) + 
                     (parseFloat(formData.bankTransfer) || 0) + 
                     (parseFloat(formData.cardSwipe) || 0);

    if (totalPaid <= 0 && (parseFloat(formData.bankTransfer) || 0) === 0) {
      toast.error('Internet breakdown. Error in reading Bill No. Please try again');
      return;
    }

    if (parseFloat(formData.balanceAmount) < 0) {
      toast.error('Negative Balance not allowed. Try again!!!');
      return;
    }

    setLoading(true);
    try {
      // Get current user name from localStorage
      const userName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'admin';
      
      // Update collection table - matching PHP structure
      const updateCollectionData = {
        userid: userName,
        paidamount: (formData.paymentMode === 'Bank Transfer' || formData.paymentMode === 'Card') ? 0 : formData.cashPayment, // Store only cash payment amount, 0 for card/bank
        btransfer: formData.bankTransfer,
        card: formData.cardSwipe,
        discount: formData.discountAmount,
        balance: formData.balanceAmount,
        mode: formData.paymentMode,
        flag: '0'
      };

      // Update final_bill table - matching PHP structure  
      const updateFinalBillData = {
        status: '0',
        discount: formData.discountAmount,
        mode: formData.paymentMode,
        paid_status: '1'
      };

      // Update collection table using barbill
      const collectionResponse = await axios.put(`/updatecommondata/collection/barbill/${formData.billNo}/`, updateCollectionData, getHeaders());
      
      // Update final_bill table using inv field
      const finalBillResponse = await axios.put(`/updatecommondata/final_bill/inv/${formData.billNo}/`, updateFinalBillData, getHeaders());
      
      if (collectionResponse.data && finalBillResponse.data) {
        toast.success('Payment saved successfully');
        
        // Get fresh setup date for form reset
        try {
          const setupDateResponse = await axios.get('/last-close-date', getHeaders());
          const setupDate = setupDateResponse.data?.close_date || 
                           setupDateResponse.data?.data?.dte ||
                           setupDateResponse.data?.data?.date;
          
          const formattedSetupDate = setupDate ? 
            new Date(setupDate).toISOString().split('T')[0] : 
            new Date().toISOString().split('T')[0];
          
          // Reset form - matching PHP reset values
          setFormData({
            date: formattedSetupDate,
            billNo: '',
            paymentMode: 'Cash',
            discountAmount: '0',
            netAmount: '0',
            cashPayment: '0',
            bankTransfer: '0',
            cardSwipe: '0',
            balanceAmount: ''
          });
        } catch (dateError) {
          // If date fetch fails, use current date
          const fallbackDate = new Date().toISOString().split('T')[0];
          setFormData({
            date: fallbackDate,
            billNo: '',
            paymentMode: 'Cash',
            discountAmount: '0',
            netAmount: '0',
            cashPayment: '0',
            bankTransfer: '0',
            cardSwipe: '0',
            balanceAmount: ''
          });
        }        setOrderFound(false);
        
        // Refresh pending records
        fetchPendingRecords();
        
        // Focus back to bill number input
        if (billNoInputRef.current) {
          billNoInputRef.current.focus();
        }
      } else {
        throw new Error('Failed to save payment');
      }
    } catch (error) {
      // console.error('Error saving payment:', error);
      toast.error('Failed to save payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Export pending records to PDF
  const exportToPDF = () => {
    if (pendingRecords.length === 0) return;

    const totalPending = pendingRecords.reduce((total, record) => total + (parseFloat(record.balance) || 0), 0);

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('PENDING PAYMENT RECORDS', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 35);
    doc.text(`Total Records: ${pendingRecords.length}`, 20, 45);
    doc.text(`Total Pending Amount: ₹${totalPending.toFixed(2)}`, 20, 55);
    
    // Table
    const tableData = pendingRecords.map(record => [
      record.id || '',
      record.tables || '',
      record.dte || '',
      record.tm || '',
      `₹${parseFloat(record.netamount || 0).toFixed(2)}`,
      `₹${parseFloat(record.paidamount || 0).toFixed(2)}`,
      `₹${parseFloat(record.balance || 0).toFixed(2)}`,
      record.mode || ''
    ]);
    
    doc.autoTable({
      head: [['Bill No.', 'Table', 'Date', 'Time', 'Net Amount', 'Paid', 'Balance', 'Mode']],
      body: tableData,
      startY: 65,
      theme: 'striped',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 }
    });
    
    doc.save(`Pending_Records_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="manual-payment-container">
      <style jsx>{`
        .payment-form-container {
          background: linear-gradient(135deg, #D4C004FF 0%, #FCFAFDFF 100%);
          border-radius: 15px;
          padding: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        .form-control-sm:focus {
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          border-color: #80bdff;
        }
        .payment-field {
          transition: all 0.3s ease;
        }
        .payment-field:hover {
          transform: translateY(-1px);
        }
        .gradient-button {
          transition: all 0.3s ease;
          transform: translateY(0px);
        }
        .gradient-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(40, 167, 69, 0.4) !important;
        }
      `}</style>
      <ToastContainer />
      
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <CardComponent style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-1">
                    <FaDollarSign className="me-2 text-success" />
                    CLUB PAYMENT COLLECTION SYSTEM
                  </h2>
                  <p className="text-muted mb-0">Manual Payment Entry</p>
                </div>
              </div>
            </CardComponent>
          </div>
        </div>

        <div className="row">
          {/* Payment Form */}
          <div className="col-lg-4 mb-4">
            <div className="payment-form-container">
              <CardComponent style={{background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '12px'}}>
                <h5 className="card-title mb-3 text-center" style={{color: '#495057', fontWeight: '600'}}>
                  <FaDollarSign className="me-2 text-success" />
                  💳 Quick Payment
                </h5>
              
              {/* Date Field - Setup Date (Read-only) */}
              <div className="mb-2 payment-field row align-items-center">
                <div className="col-4">
                  <label htmlFor="date" className="form-label fw-semibold text-muted small mb-0">
                    📅 Date
                  </label>
                </div>
                <div className="col-8">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    id="date"
                    value={formData.date}
                    readOnly
                    disabled
                    style={{
                      fontSize: '14px',
                      backgroundColor: '#e9ecef',
                      border: '2px solid #6c757d',
                      color: '#495057',
                      cursor: 'not-allowed',
                      fontWeight: '600'
                    }}
                    title={loadingSetupDate ? 'Loading business date from setup table...' : `Business date fetched from sale_date_setup table: ${setupDate} (Read-only)`}
                  />
                </div>
               
              </div>

              {/* Bill Number Field - Compact */}
              <div className="mb-2 payment-field row align-items-center">
                <div className="col-4">
                  <label htmlFor="billNo" className="form-label fw-semibold text-muted small mb-0">🧾 Bill No.</label>
                </div>
                <div className="col-8">
                  <div className="input-group input-group-sm">
                  <input
                    ref={billNoInputRef}
                    type="text"
                    className="form-control"
                    id="billNo"
                    value={formData.billNo}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange('billNo', value);
                      
                      // Provide immediate feedback
                      if (value.length >= 3) {
                        // console.log('✍️ Typing bill number:', value, '- Will search in 500ms');
                      } else if (value.length === 0) {
                        // Reset when field is cleared
                        if (orderFound) {
                          setOrderFound(false);
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleBillSearch();
                      }
                    }}
                    placeholder={loading ? "Searching..." : "Enter bill number (min 3 digits)"}
                    style={{fontSize: '14px', backgroundColor: loading ? '#f8f9fa' : 'white'}}
                    disabled={loading}
                  />
                    {/* <button
                      className="btn btn-warning btn-sm"
                      type="button"
                      onClick={handleBillSearch}
                      disabled={loading}
                      style={{fontWeight: '600'}}
                    >
                      {loading ? <FaSpinner className="fa-spin" /> : 'Search'}
                    </button> */}
                  </div>
                </div>
              </div>

              {/* Payment Mode Field - Compact */}
              <div className="mb-2 payment-field row align-items-center">
                <div className="col-4">
                  <label htmlFor="paymentMode" className="form-label fw-semibold text-muted small mb-0">💳 Payment Mode</label>
                </div>
                <div className="col-8">
                  <select
                    className="form-select form-select-sm"
                    id="paymentMode"
                    value={formData.paymentMode}
                    onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                    style={{fontSize: '14px', fontWeight: '500'}}
                  >
                    <option value="Cash">💰 Cash</option>
                    <option value="Card">💳 Card</option>
                    <option value="Bank Transfer">🏦 Bank Transfer</option>
                    <option value="Mixed">🔄 Mixed</option>
                  </select>
                </div>
              </div>

              {/* Discount Amount Field - Compact */}
              <div className="mb-2 payment-field row align-items-center">
                <div className="col-4">
                  <label htmlFor="discountAmount" className="form-label fw-semibold text-danger small mb-0">💰 Discount (฿)</label>
                </div>
                <div className="col-8">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="discountAmount"
                    value={formData.discountAmount}
                    onChange={(e) => handleInputChange('discountAmount', e.target.value)}
                    step="0.01"
                    min="0"
                    style={{fontSize: '14px', backgroundColor: '#fff5f5', border: '2px solid #f56565', fontWeight: '600'}}
                  />
                </div>
              </div>

              {/* Net Amount Field - Horizontal Layout */}
              <div className="mb-2 payment-field row align-items-center">
                <div className="col-4">
                  <label htmlFor="netAmount" className="form-label fw-semibold text-warning small mb-0">💵 Net Amount (฿)</label>
                </div>
                <div className="col-8">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="netAmount"
                    value={formData.netAmount}
                    onChange={(e) => handleInputChange('netAmount', e.target.value)}
                    step="0.01"
                    min="0"
                    readOnly={orderFound}
                    style={{fontSize: '14px', backgroundColor: '#fff8dc', border: '2px solid #ffc107', fontWeight: '600'}}
                  />
                </div>
              </div>

              {/* Cash Payment Field - Show for Cash or Mixed */}
              {(formData.paymentMode === 'Cash' || formData.paymentMode === 'Mixed') && (
                <div className="mb-2 payment-field row align-items-center">
                  <div className="col-4">
                    <label htmlFor="cashPayment" className="form-label fw-semibold text-success small mb-0">💰 Cash</label>
                  </div>
                  <div className="col-8">
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      id="cashPayment"
                      value={formData.cashPayment}
                      onChange={(e) => handleInputChange('cashPayment', e.target.value)}
                      onBlur={() => {
                        // Auto-calculate balance on blur (when user finishes entering)
                        const netAmount = parseFloat(formData.netAmount) || 0;
                        const cashPayment = parseFloat(formData.cashPayment) || 0;
                        const bankTransfer = parseFloat(formData.bankTransfer) || 0;
                        const cardSwipe = parseFloat(formData.cardSwipe) || 0;
                        const discountAmount = parseFloat(formData.discountAmount) || 0;
                        
                        const totalPaid = cashPayment + bankTransfer + cardSwipe;
                        const balanceAmount = (netAmount - discountAmount) - totalPaid;
                        
                        setFormData(prev => ({
                          ...prev,
                          balanceAmount: balanceAmount.toFixed(2)
                        }));
                        
                        if (balanceAmount <= 0) {
                          toast.success('Payment completed! Balance: ฿0');
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur(); // Trigger onBlur calculation
                        }
                      }}
                      step="0.01"
                      min="0"
                      style={{fontSize: '14px', backgroundColor: '#f0fff4', border: '2px solid #28a745', fontWeight: '600'}}
                    />
                  </div>
                </div>
              )}

              {/* Bank Transfer Field - Show for Bank Transfer or Mixed */}
              {(formData.paymentMode === 'Bank Transfer' || formData.paymentMode === 'Mixed') && (
                <div className="mb-2 payment-field row align-items-center">
                  <div className="col-4">
                    <label htmlFor="bankTransfer" className="form-label fw-semibold text-info small mb-0">🏦 Bank</label>
                  </div>
                  <div className="col-8">
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      id="bankTransfer"
                      value={formData.bankTransfer}
                      onChange={(e) => handleInputChange('bankTransfer', e.target.value)}
                      onBlur={() => {
                        // Auto-calculate balance on blur
                        const netAmount = parseFloat(formData.netAmount) || 0;
                        const cashPayment = parseFloat(formData.cashPayment) || 0;
                        const bankTransfer = parseFloat(formData.bankTransfer) || 0;
                        const cardSwipe = parseFloat(formData.cardSwipe) || 0;
                        const discountAmount = parseFloat(formData.discountAmount) || 0;
                        
                        const totalPaid = cashPayment + bankTransfer + cardSwipe;
                        const balanceAmount = (netAmount - discountAmount) - totalPaid;
                        
                        setFormData(prev => ({
                          ...prev,
                          balanceAmount: balanceAmount.toFixed(2)
                        }));
                        
                        if (balanceAmount <= 0) {
                          toast.success('Payment completed! Balance: ฿0');
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur(); // Trigger onBlur calculation
                        }
                      }}
                      step="0.01"
                      min="0"
                      style={{fontSize: '14px', backgroundColor: '#f0f8ff', border: '2px solid #17a2b8', fontWeight: '600'}}
                    />
                  </div>
                </div>
              )}

              {/* Card Payment Field - Show for Card or Mixed */}
              {(formData.paymentMode === 'Card' || formData.paymentMode === 'Mixed') && (
                <div className="mb-2 payment-field row align-items-center">
                  <div className="col-4">
                    <label htmlFor="cardSwipe" className="form-label fw-semibold text-primary small mb-0">💳 Card</label>
                  </div>
                  <div className="col-8">
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      id="cardSwipe"
                      value={formData.cardSwipe}
                      onChange={(e) => handleInputChange('cardSwipe', e.target.value)}
                      onBlur={() => {
                        // Auto-calculate balance on blur
                        const netAmount = parseFloat(formData.netAmount) || 0;
                        const cashPayment = parseFloat(formData.cashPayment) || 0;
                        const bankTransfer = parseFloat(formData.bankTransfer) || 0;
                        const cardSwipe = parseFloat(formData.cardSwipe) || 0;
                        const discountAmount = parseFloat(formData.discountAmount) || 0;
                        
                        const totalPaid = cashPayment + bankTransfer + cardSwipe;
                        const balanceAmount = (netAmount - discountAmount) - totalPaid;
                        
                        setFormData(prev => ({
                          ...prev,
                          balanceAmount: balanceAmount.toFixed(2)
                        }));
                        
                        
                        if (balanceAmount <= 0) {
                          toast.success('Payment completed! Balance: ฿0');
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur(); // Trigger onBlur calculation
                        }
                      }}
                      step="0.01"
                      min="0"
                      style={{fontSize: '14px', backgroundColor: '#f0f4ff', border: '2px solid #007bff', fontWeight: '600'}}
                    />
                  </div>
                </div>
              )}

              {/* Balance Field - Horizontal Layout */}
              <div className="mb-2 payment-field row align-items-center">
                <div className="col-4">
                  <label htmlFor="balanceAmount" className="form-label fw-semibold text-danger small mb-0">⚖️ Balance</label>
                </div>
                <div className="col-8">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="balanceAmount"
                    value={formData.balanceAmount}
                    onChange={(e) => handleInputChange('balanceAmount', e.target.value)}
                    step="0.01"
                    readOnly
                    style={{fontSize: '14px', backgroundColor: '#fff5f5', border: '2px solid #dc3545', fontWeight: '600'}}
                  />
                </div>
              </div>

              {/* Save Button - Compact and Attractive */}
              <div className="d-grid mt-3">
                <button
                    className="btn btn-success gradient-button"
                    onClick={handleSavePayment}
                    disabled={loading || !formData.billNo.trim()}
                    style={{
                      background: 'linear-gradient(45deg, #28a745, #20c997)',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '15px',
                      padding: '12px',
                      borderRadius: '10px',
                      boxShadow: '0 4px 8px rgba(40, 167, 69, 0.3)'
                    }}
                >
                  {loading ? (
                    <>
                      <FaSpinner className="fa-spin me-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      💾 Save Payment
                    </>
                  )}
                </button>
              </div>
            </CardComponent>
            </div>
          </div>

          {/* Pending Records Table */}
          <div className="col-lg-8">
            <CardComponent 
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <span>{`Pending Payments (${pendingRecords.length} records with balance > 0)`}</span>
                  <small className="badge bg-info">
                    🔄 Auto-refresh: 1 min
                  </small>
                </div>
              }
              headerColor="warning" 
              pull="left"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={exportToPDF}
                    disabled={pendingRecords.length === 0}
                  >
                    <FaFilePdf className="me-1" />
                    Export PDF
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={fetchPendingRecords}
                    disabled={loadingPending}
                    title="Manual refresh - Auto-refresh every minute"
                  >
                    {loadingPending ? (
                      <FaSpinner className="fa-spin" />
                    ) : (
                      <>🔄 Refresh</>
                    )}
                  </button>
                </div>
              </div>

              {loadingPending ? (
                <div className="text-center py-4">
                  <FaSpinner className="fa-spin fa-2x text-primary" />
                  <p className="mt-2">Loading pending records...</p>
                </div>
              ) : pendingRecords.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <FaSave size={32} className="mb-3 text-success" />
                  <h6>No Pending Payments</h6>
                  <p>All orders have been fully paid!</p>
                </div>
              ) : (
                <div className="table-responsive" style={{maxHeight: '600px', overflowY: 'auto'}}>
                  <table className="table table-striped table-hover">
                    <thead className="table-warning">
                      <tr>
                        <th>Bill No.</th>
                        <th>Table</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Net Amount</th>
                        <th>💰 Cash</th>
                        <th>🏦 Bank</th>
                        <th>💳 Card</th>
                        <th>Balance</th>
                        <th>Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRecords.map((record, index) => (
                        <tr key={record.id || index}>
                          <td>
                            <strong className="text-primary">{record.barbill || record.id}</strong>
                          </td>
                          <td>Table {record.tables}</td>
                          <td>{record.dte}</td>
                          <td>{record.tme ? record.tme.split('.')[0] : ''}</td>
                          <td>฿{parseFloat(record.netamount || 0).toLocaleString()}</td>
                          <td>
                            <span className="badge bg-success">
                              ฿{parseFloat(record.paidamount || 0).toLocaleString()}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-info">
                              ฿{parseFloat(record.btransfer || 0).toLocaleString()}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-primary">
                              ฿{parseFloat(record.card || 0).toLocaleString()}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-warning text-dark">
                              ฿{parseFloat(record.balance || 0).toLocaleString()}
                            </span>
                          </td>
                          <td>
                            {record.mode ? (
                              <span className={`badge ${
                                record.mode === 'cash' ? 'bg-success' : 
                                record.mode === 'card' ? 'bg-primary' : 'bg-info'
                              }`}>
                                {record.mode.toUpperCase()}
                              </span>
                            ) : (
                              <span className="badge bg-secondary">PENDING</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-warning" style={{backgroundColor: '#fff3cd'}}>
                        <td colSpan="8" className="text-end fw-bold fs-5" style={{fontWeight: '700'}}>
                          Total Pending Amount:
                        </td>
                        <td className="fw-bold" style={{backgroundColor: '#dc3545', fontWeight: '700'}}>
                          <span className="badge bg-danger fs-5" style={{fontSize: '1.1rem !important', fontWeight: '700'}}>
                            ฿{pendingRecords.reduce((total, record) => total + (parseFloat(record.balance) || 0), 0).toLocaleString()}
                          </span>
                        </td>
                        <td colSpan="1" style={{backgroundColor: '#fff3cd'}}></td>
                      </tr>
                    </tfoot>
                  </table>
                  

                </div>
              )}
            </CardComponent>
          </div>
        </div>
      </div>

      {/* Floating Navigation */}
      <FloatingNavModal />
    </div>
  );
};

export default ManualPayment;